// The Veteran action log (PLAN-04 S1, REFOCUS §11 anti-cheat).
//
// The phone uploads only what the player did, and the server (PLAN-05) replays
// it against the league seed. So every player action that changes the sim is
// recorded here, by `performAction`, never by a screen: the store calls it for
// live play and `replayActionLog` calls it for replay, so the two cannot drift.

import type { GameState, VeteranAction } from '../core/types';
import { applyAction, type ActionResult, type SimAction } from './actions';
import { stepSimulation, type StepReport } from './simulate';

/**
 * Applies one player action and, in Veteran, appends it to the action log.
 *
 * The one entry point for anything a player does to the sim: the store calls
 * it for live play and `replayActionLog` calls it for replay, so the two can
 * never drift apart. The action is logged before it is applied so a replay
 * sees the same log at every point, including the actions that fail.
 */
export function performAction(state: GameState, action: SimAction): ActionResult {
  if (state.veteran.enabled) {
    const entry: VeteranAction = {
      tick: state.tickCount,
      type: action.type,
      // A plain-JSON copy: the log is uploaded as-is (PLAN-05 R4).
      args: JSON.parse(JSON.stringify(action.args ?? {})) as Record<string, unknown>,
    };
    (state.veteran.actions ??= []).push(entry);
  }
  return applyAction(state, action);
}

/** Turns a logged entry back into the action that produced it. */
export function actionFromLog(entry: VeteranAction): SimAction {
  return { type: entry.type, args: entry.args } as SimAction;
}

export interface ReplayOptions {
  /** How many ticks to run. Tick `t` steps the sim, then applies the actions logged at `t`. */
  ticks: number;
  /** Sim-hours each tick advances. */
  hoursPerTick: number;
  /** Sees every step's report, for tests that want more than the end state. */
  onStep?: (report: StepReport) => void;
}

/**
 * Replays an action log from a season-start state.
 *
 * Mirrors the live loop in `app/game.ts`: stamp the tick, step the world, and
 * the player's taps land between ticks against the tick just stamped. The input
 * state is not touched; the replayed state is returned.
 */
export function replayActionLog(
  start: GameState,
  log: readonly VeteranAction[],
  options: ReplayOptions,
): GameState {
  const state = structuredClone(start) as GameState;
  // Replay goes through `performAction`, so it rebuilds the log as it goes and
  // the replayed state carries the same log the live one did.
  const byTick = new Map<number, VeteranAction[]>();
  for (const entry of log) {
    const list = byTick.get(entry.tick) ?? [];
    list.push(entry);
    byTick.set(entry.tick, list);
  }
  const applyAt = (tick: number) => {
    for (const entry of byTick.get(tick) ?? []) performAction(state, actionFromLog(entry));
  };

  applyAt(state.tickCount);
  const first = state.tickCount;
  for (let t = first + 1; t <= first + options.ticks; t++) {
    state.tickCount = t;
    const report = stepSimulation(state, { hours: options.hoursPerTick });
    options.onStep?.(report);
    applyAt(t);
  }
  return state;
}
